import { useState } from "react";
import { fmtMXNFull, fmtDate } from "@/lib/api";
import IncontiLogo from "@/components/IncontiLogo";
import SignaturePad from "@/components/SignaturePad";
import { PenLine } from "lucide-react";

// Convierte número a palabras (es-MX) — soporta hasta millones
function numberToSpanish(num) {
  if (num === 0) return "CERO";
  const ones = ["", "UNO", "DOS", "TRES", "CUATRO", "CINCO", "SEIS", "SIETE", "OCHO", "NUEVE",
    "DIEZ", "ONCE", "DOCE", "TRECE", "CATORCE", "QUINCE", "DIECISÉIS", "DIECISIETE", "DIECIOCHO", "DIECINUEVE",
    "VEINTE", "VEINTIUNO", "VEINTIDÓS", "VEINTITRÉS", "VEINTICUATRO", "VEINTICINCO", "VEINTISÉIS", "VEINTISIETE", "VEINTIOCHO", "VEINTINUEVE"];
  const tens = ["", "", "", "TREINTA", "CUARENTA", "CINCUENTA", "SESENTA", "SETENTA", "OCHENTA", "NOVENTA"];
  const hundreds = ["", "CIENTO", "DOSCIENTOS", "TRESCIENTOS", "CUATROCIENTOS", "QUINIENTOS", "SEISCIENTOS", "SETECIENTOS", "OCHOCIENTOS", "NOVECIENTOS"];

  function under1000(n) {
    if (n === 0) return "";
    if (n === 100) return "CIEN";
    let result = "";
    const h = Math.floor(n / 100);
    const r = n % 100;
    if (h > 0) result += hundreds[h] + " ";
    if (r < 30) result += ones[r];
    else {
      const t = Math.floor(r / 10);
      const o = r % 10;
      result += tens[t] + (o > 0 ? " Y " + ones[o] : "");
    }
    return result.trim();
  }

  const intPart = Math.floor(num);
  const cents = Math.round((num - intPart) * 100);
  let words = "";
  if (intPart >= 1000000) {
    const mill = Math.floor(intPart / 1000000);
    words += (mill === 1 ? "UN MILLÓN" : under1000(mill) + " MILLONES") + " ";
  }
  const thousands = Math.floor((intPart % 1000000) / 1000);
  if (thousands > 0) {
    words += (thousands === 1 ? "MIL" : under1000(thousands) + " MIL") + " ";
  }
  const remainder = intPart % 1000;
  if (remainder > 0) words += under1000(remainder);
  return `${words.trim() || "CERO"} PESOS ${cents.toString().padStart(2, "0")}/100 M.N.`;
}

const METHOD_LABELS = { deposito: "DEPÓSITO", transferencia: "TRANSFERENCIA", efectivo: "EFECTIVO" };

const ROW = ({ label, value, bold, color }) => (
  <div className="flex justify-between py-2 border-b" style={{ borderColor: "#e5e7eb" }}>
    <span className="text-slate-600 text-sm">{label}</span>
    <span className={`tabular-nums ${bold ? "font-bold" : ""}`} style={{ color: color || "#0f172a" }}>{value}</span>
  </div>
);

export default function PayrollReceiptSheet({ entry, submitSignature }) {
  const [showPad, setShowPad] = useState(false);
  const [signing, setSigning] = useState(false);

  const sign = async (dataUrl) => {
    setSigning(true);
    try {
      await submitSignature(dataUrl);
      setShowPad(false);
    } finally {
      setSigning(false);
    }
  };

  const folio = `INC-${entry.id.slice(0, 8).toUpperCase()}`;
  const isObra = entry.payroll_type === "obra";
  const paidWhen = entry.paid_date || entry.paid_at;

  return (
    <>
      <style>{`
        @media print {
          body { background: white !important; }
          .no-print { display: none !important; }
          .receipt { box-shadow: none !important; margin: 0 !important; max-width: 100% !important; }
        }
      `}</style>
      <div className="receipt mx-auto bg-white text-slate-900 max-w-[820px] shadow-2xl" style={{ minHeight: "1100px" }}>
        <div className="px-10 pt-10 pb-6 border-b-4" style={{ borderColor: "#3F2A91" }}>
          <div className="flex items-start justify-between">
            <div>
              <IncontiLogo size={56} />
              <div className="mt-3 text-xs text-slate-500 leading-snug">
                CORPORATION INCONTI · administracion@inconti.net
              </div>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-widest text-slate-400 font-mono">// FOLIO</div>
              <div className="font-mono text-lg font-bold text-slate-900">{folio}</div>
              <div className="mt-2 text-[11px] uppercase tracking-widest text-slate-400 font-mono">// EMITIDO</div>
              <div className="font-mono text-xs text-slate-700">{fmtDate(new Date().toISOString())}</div>
            </div>
          </div>
        </div>

        <div className="px-10 py-6 text-center">
          <h1 className="text-3xl font-display font-black tracking-widest" style={{ color: "#3F2A91" }}>
            RECIBO DE NÓMINA
          </h1>
          <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">
            {isObra ? "Personal de obra" : "Personal administrativo"}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 px-10 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono mb-1">EMPLEADO</div>
            <div className="font-bold text-lg text-slate-900">{entry.employee?.name || entry.employee_name}</div>
            {entry.employee?.position && <div className="text-sm text-slate-600">{entry.employee.position}</div>}
            {entry.employee?.rfc && <div className="text-xs text-slate-500 font-mono mt-1">RFC: {entry.employee.rfc}</div>}
            {entry.employee?.bank_account && <div className="text-xs text-slate-500 font-mono">CUENTA: {entry.employee.bank_account}</div>}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono mb-1">PERÍODO DE PAGO</div>
            <div className="font-bold text-base text-slate-900 font-mono">{fmtDate(entry.period_start)} → {fmtDate(entry.period_end)}</div>
            <div className="text-xs text-slate-500 mt-1 capitalize">Frecuencia: {entry.employee?.payment_period || "—"}</div>
            {entry.payment_method && (
              <div className="text-xs text-slate-600 mt-1"><b>Método de pago:</b> {METHOD_LABELS[entry.payment_method] || entry.payment_method}</div>
            )}
            {paidWhen && (
              <div className="text-xs text-slate-600"><b>Fecha de pago:</b> {fmtDate(paidWhen)}</div>
            )}
            {isObra && entry.project && (
              <div className="mt-2 px-2 py-1 inline-block rounded text-xs font-semibold" style={{ background: "#3F2A9120", color: "#3F2A91" }}>
                PROYECTO: {entry.project.name}
              </div>
            )}
          </div>
        </div>

        <div className="px-10 mb-6">
          <div className="border rounded" style={{ borderColor: "#cbd5e1" }}>
            <div className="px-4 py-2 text-[11px] uppercase tracking-widest font-mono text-white"
              style={{ background: "#3F2A91" }}>
              Concepto · Monto
            </div>
            <div className="px-4 py-3">
              <ROW label="Sueldo bruto" value={fmtMXNFull(entry.gross)} />
              {entry.bonuses > 0 && <ROW label="Bonos" value={`+ ${fmtMXNFull(entry.bonuses)}`} color="#16a34a" />}
              {(entry.extra_items || []).map((it, i) => (
                <ROW key={i} label={it.label} value={`${it.type === "percepcion" ? "+" : "-"} ${fmtMXNFull(it.amount)}`} color={it.type === "percepcion" ? "#16a34a" : "#dc2626"} />
              ))}
              {((entry.deductions || 0) - (entry.loan_deduction || 0)) > 0 && <ROW label={entry.loan_deduction > 0 ? "Deducciones (otros)" : "Deducciones"} value={`- ${fmtMXNFull((entry.deductions || 0) - (entry.loan_deduction || 0))}`} color="#dc2626" />}
              {entry.loan_deduction > 0 && <ROW label="Descuento préstamo/adelanto" value={`- ${fmtMXNFull(entry.loan_deduction)}`} color="#dc2626" />}
              {entry.isr_deduction > 0 && <ROW label="Retención ISR (honorarios asimilados)" value={`- ${fmtMXNFull(entry.isr_deduction)}`} color="#dc2626" />}
              <div className="flex justify-between py-3 mt-2 px-3 rounded" style={{ background: "#3F2A91", color: "white" }}>
                <span className="font-bold uppercase tracking-widest text-sm">NETO A PAGAR</span>
                <span className="font-display font-black text-2xl tabular-nums">{fmtMXNFull(entry.net)}</span>
              </div>
            </div>
          </div>
          <div className="mt-3 px-3 py-2 text-xs italic" style={{ background: "#f8fafc", border: "1px dashed #cbd5e1" }}>
            <span className="font-semibold">SON: </span>{numberToSpanish(entry.net)}
          </div>
        </div>

        {entry.notes && (
          <div className="px-10 mb-6">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono mb-1">OBSERVACIONES</div>
            <div className="text-sm text-slate-700 italic">{entry.notes}</div>
          </div>
        )}

        <div className="px-10 mb-8">
          <div className="text-center py-2 rounded text-xs uppercase tracking-widest font-bold"
            style={{
              background: entry.status === "pagado" ? "#16a34a" : entry.status === "cancelado" ? "#dc2626" : "#94a3b8",
              color: "white",
            }}>
            ESTADO: {entry.status}{paidWhen ? ` · ${fmtDate(paidWhen)}` : ""}{entry.payment_method ? ` · ${METHOD_LABELS[entry.payment_method] || entry.payment_method}` : ""}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-12 px-10 mb-12">
          <div className="text-center">
            <div className="border-t-2 pt-2 mt-12" style={{ borderColor: "#0f172a" }}>
              <div className="text-xs uppercase tracking-widest font-bold">PAGADO POR</div>
              <div className="text-sm mt-1 font-semibold">{entry.paid_by_name || "________________________"}</div>
              <div className="text-[10px] text-slate-500 mt-1">CORPORATION INCONTI</div>
            </div>
          </div>
          <div className="text-center">
            <div className="border-t-2 pt-2 mt-12" style={{ borderColor: "#0f172a", position: "relative" }}>
              {entry.signature && (
                <img src={entry.signature} alt="Firma" style={{ position: "absolute", bottom: "100%", left: "50%", transform: "translateX(-50%)", height: 70, maxWidth: "90%", objectFit: "contain" }} />
              )}
              <div className="text-xs uppercase tracking-widest font-bold">RECIBÍ DE CONFORMIDAD</div>
              <div className="text-sm mt-1 font-semibold">{entry.signed_name || entry.employee?.name || entry.employee_name}</div>
              <div className="text-[10px] text-slate-500 mt-1">
                {entry.signature ? `FIRMADO DIGITALMENTE · ${fmtDate(entry.signed_at)}` : "FIRMA DEL EMPLEADO"}
              </div>
              {!entry.signature && (
                <button
                  type="button"
                  data-testid="sign-receipt-btn"
                  onClick={() => setShowPad(true)}
                  className="no-print mt-3 px-4 py-2 rounded text-xs uppercase tracking-widest font-bold text-white inline-flex items-center gap-2"
                  style={{ background: "#3F2A91" }}
                >
                  <PenLine className="w-3.5 h-3.5" /> Firmar de recibido
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="px-10 py-4 text-center text-[10px] text-slate-400 border-t" style={{ borderColor: "#e5e7eb" }}>
          Documento generado por Corporation Inconti ERP · Folio {folio} · {fmtDate(new Date().toISOString())}
        </div>
      </div>
      {showPad && (
        <SignaturePad
          title={`${entry.employee?.name || entry.employee_name} · ${fmtMXNFull(entry.net)}`}
          onConfirm={sign}
          onClose={() => setShowPad(false)}
          saving={signing}
        />
      )}
    </>
  );
}
